// Shared unlocked AudioContext — mobile browsers require a user gesture to allow audio.
let _sharedCtx = null;
let _unlocked = false;

function _getCtx() {
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) return null;
  if (!_sharedCtx) _sharedCtx = new AC();
  return _sharedCtx;
}

/**
 * Attach a one-shot listener to unlock audio on the first user gesture (touch, click, keydown).
 * Call this once at app-mount (or on route change) — it's idempotent.
 */
export function initAudioUnlock() {
  if (_unlocked) return;
  const unlock = () => {
    try {
      const ctx = _getCtx();
      if (!ctx) return;
      if (ctx.state === 'suspended') ctx.resume();
      // Play a 1ms silent buffer to fully unlock iOS/Android WebAudio
      const buf = ctx.createBuffer(1, 1, 22050);
      const src = ctx.createBufferSource();
      src.buffer = buf;
      src.connect(ctx.destination);
      src.start(0);
      _unlocked = true;
      ['touchstart', 'touchend', 'click', 'keydown'].forEach(ev =>
        window.removeEventListener(ev, unlock, true)
      );
    } catch { /* ignore */ }
  };
  ['touchstart', 'touchend', 'click', 'keydown'].forEach(ev =>
    window.addEventListener(ev, unlock, { capture: true, once: false })
  );
}

// Westminster-style two-tone doorbell using WebAudio.
export function playDoorbell(volume = 0.35) {
  try {
    const ctx = _getCtx();
    if (!ctx) return;
    if (ctx.state === 'suspended') ctx.resume();
    const notes = [
      { f: 659.25, t: 0, d: 0.28 }, // E5
      { f: 523.25, t: 0.35, d: 0.55 }, // C5 longer
    ];
    notes.forEach(({ f, t, d }) => {
      const now = ctx.currentTime + t;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(f, now);
      // add slight overtone
      const osc2 = ctx.createOscillator();
      osc2.type = 'triangle';
      osc2.frequency.setValueAtTime(f * 2, now);
      const gain2 = ctx.createGain();
      gain.gain.setValueAtTime(0, now);
      gain.gain.linearRampToValueAtTime(volume, now + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + d);
      gain2.gain.setValueAtTime(0, now);
      gain2.gain.linearRampToValueAtTime(volume * 0.4, now + 0.02);
      gain2.gain.exponentialRampToValueAtTime(0.0001, now + d);
      osc.connect(gain).connect(ctx.destination);
      osc2.connect(gain2).connect(ctx.destination);
      osc.start(now); osc.stop(now + d + 0.02);
      osc2.start(now); osc2.stop(now + d + 0.02);
    });
  } catch { /* ignore */ }
}

// Short soft chime for chat/message notifications — deliberately different from doorbell.
export function playChime(volume = 0.32) {
  try {
    const ctx = _getCtx();
    if (!ctx) return;
    if (ctx.state === 'suspended') ctx.resume();
    const notes = [
      { f: 880.0, t: 0, d: 0.18 },   // A5
      { f: 1174.66, t: 0.12, d: 0.32 }, // D6
    ];
    notes.forEach(({ f, t, d }) => {
      const now = ctx.currentTime + t;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(f, now);
      gain.gain.setValueAtTime(0, now);
      gain.gain.linearRampToValueAtTime(volume, now + 0.015);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + d);
      osc.connect(gain).connect(ctx.destination);
      osc.start(now); osc.stop(now + d + 0.02);
    });
  } catch { /* ignore */ }
}
